import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import { format } from "date-fns"

export const downloadInvoicePDF = (Invoice, Colour) => {
  const doc = new jsPDF()
  let yPosition = 20

  doc.setFontSize(18)
  doc.setTextColor(Colour || "#3513f1")
  doc.text("INVOICE", 105, yPosition, { align: "center" })
  yPosition += 15

  doc.setFontSize(12)
  doc.setTextColor(0, 0, 0)
  doc.text(Invoice?.vendor?.vendor_name || "", 14, yPosition)
  yPosition += 6

  doc.setFontSize(9)
  if (Invoice?.vendor?.email) {
    doc.text(Invoice.vendor.email, 14, yPosition)
    yPosition += 5
  }
  if (Invoice?.vendor?.phone) {
    const phone = Invoice.vendor.phone.startsWith("+234")
      ? Invoice.vendor.phone
      : `+234 (0) ${Invoice.vendor.phone}`
    doc.text(phone, 14, yPosition)
    yPosition += 5
  }

  yPosition += 5

  doc.setFontSize(10)
  doc.setFont(undefined, "bold")
  doc.text("BILL TO:", 14, yPosition)
  yPosition += 5
  doc.setFont(undefined, "normal")

  Invoice?.grn?.stores?.forEach(store => {
    doc.text(`${store.store_name} (${store.byd_cost_center_code})`, 14, yPosition)
    yPosition += 5
    doc.text(store.store_email, 14, yPosition)
    yPosition += 5
  })

  yPosition += 5

  const summaryData = [
    ["Invoice Date:", format(new Date(Invoice?.date_created), "dd-MMM-yyyy")],
    ["PO Number:", Invoice?.grn?.purchase_order?.po_id || "-"],
    ["GRN Number:", Invoice?.grn?.grn_number || "-"],
    ["External Reference:", Invoice?.external_document_id || "-"],
  ]

  summaryData.forEach(([label, value]) => {
    doc.setFont(undefined, "bold")
    doc.text(label, 14, yPosition)
    doc.setFont(undefined, "normal")
    doc.text(String(value), 70, yPosition)
    yPosition += 6
  })

  yPosition += 5

  const tableData = Invoice.invoice_line_items.map((item, index) => [
    index + 1,
    item.grn_line_item.purchase_order_line_item.product_name,
    parseFloat(item.grn_line_item.purchase_order_line_item.unit_price).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    parseFloat(item.grn_line_item.purchase_order_line_item.quantity).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) +
      " " +
      item.grn_line_item.purchase_order_line_item.unit_of_measurement +
      "s",
    parseFloat(item.quantity).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) +
      " " +
      item.grn_line_item.purchase_order_line_item.unit_of_measurement +
      "s",
    item.discount ? item.discount : "0.00",
    parseFloat(item.gross_total).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    parseFloat(item.tax_amount).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    parseFloat(item.net_total).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
  ])

  autoTable(doc, {
    startY: yPosition,
    head: [
      [
        "SN",
        "Description",
        "Unit Price (NGN)",
        "Ordered Qty",
        "Delivered Qty",
        "Discount",
        "Gross Total (NGN)",
        "Tax (NGN)",
        "Net Total (NGN)",
      ],
    ],
    body: tableData,
    styles: {
      fontSize: 8,
      cellPadding: 2,
    },
    headStyles: {
      fillColor: Colour ? hexToRgb(Colour) : [53, 19, 241],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 10 },
      1: { halign: "left", cellWidth: 40 },
      2: { halign: "right", cellWidth: 20 },
      3: { halign: "center", cellWidth: 20 },
      4: { halign: "center", cellWidth: 20 },
      5: { halign: "center", cellWidth: 17 },
      6: { halign: "right", cellWidth: 20 },
      7: { halign: "right", cellWidth: 18 },
      8: { halign: "right", cellWidth: 20 },
    },
  })

  const finalY = doc.lastAutoTable.finalY + 10

  // Vendor Initials Section (left side)
  const vendorInitials = Invoice?.vendor?.vendor_name
    .split(" ")
    .map(name => name[0])
    .join(".")

  doc.setFontSize(14)
  doc.setFont(undefined, "bold")
  doc.text(vendorInitials, 14, finalY)
  
  doc.setFontSize(9)
  doc.setFont(undefined, "normal")
  doc.text("Vendor's Initials", 14, finalY + 6)
  
  doc.setFontSize(8)
  doc.text(
    "Created On " + format(new Date(Invoice?.due_date || Invoice?.date_created), "dd-MM-yyyy"),
    14,
    finalY + 11
  )

  // Totals Section (right side)
  doc.setFontSize(10)
  doc.setFont(undefined, "bold")
  doc.text("Total (minus Tax):", 120, finalY)
  doc.text(
    "NGN " +
      parseFloat(Invoice.net_total).toLocaleString("en-NG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    190,
    finalY,
    { align: "right" }
  )

  doc.text("Tax Amount:", 120, finalY + 6)
  doc.text(
    "NGN " +
      parseFloat(Invoice.total_tax_amount).toLocaleString("en-NG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    190,
    finalY + 6,
    { align: "right" }
  )

  doc.setFontSize(12)
  doc.text("Total (incl. Tax):", 120, finalY + 14)
  doc.text(
    "NGN " +
      parseFloat(Invoice.gross_total).toLocaleString("en-NG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    190,
    finalY + 14,
    { align: "right" }
  )

  doc.save(
    `Invoice-${Invoice?.external_document_id || Invoice?.grn?.grn_number || "document"}.pdf`
  )
}

export const downloadGRNPDF = (receipt, Colour) => {
  const doc = new jsPDF()
  let yPosition = 20

  doc.setFontSize(18)
  doc.setTextColor(Colour || "#3513f1")
  doc.text("GOODS RECEIVED NOTE", 105, yPosition, { align: "center" })
  yPosition += 15

  doc.setFontSize(11)
  doc.setTextColor(0, 0, 0)
  doc.setFont(undefined, "bold")
  doc.text("RECEIVED FROM:", 14, yPosition)
  yPosition += 6

  doc.setFontSize(10)
  doc.setFont(undefined, "normal")
  doc.text(receipt?.vendor?.vendor_name || "", 14, yPosition)
  yPosition += 5

  if (receipt?.vendor?.email) {
    doc.text(receipt.vendor.email, 14, yPosition)
    yPosition += 5
  }
  if (receipt?.vendor?.phone) {
    const phone = receipt.vendor.phone.startsWith("+234")
      ? receipt.vendor.phone
      : `+234 (0) ${receipt.vendor.phone}`
    doc.text(phone, 14, yPosition)
    yPosition += 5
  }

  yPosition += 5

  doc.setFont(undefined, "bold")
  doc.text("RECEIVED AT:", 14, yPosition)
  yPosition += 6
  doc.setFont(undefined, "normal")

  receipt?.stores?.forEach(store => {
    doc.text(`${store.store_name} (${store.byd_cost_center_code})`, 14, yPosition)
    yPosition += 5
    doc.text(store.store_email, 14, yPosition)
    yPosition += 5
  })

  yPosition += 5

  const summaryData = [
    ["GRN Date:", format(new Date(receipt?.created), "dd-MMM-yyyy")],
    ["PO Number:", receipt?.purchase_order?.po_id || "-"],
    ["GRN Number:", receipt?.grn_number || "-"],
  ]

  summaryData.forEach(([label, value]) => {
    doc.setFont(undefined, "bold")
    doc.text(label, 14, yPosition)
    doc.setFont(undefined, "normal")
    doc.text(String(value), 70, yPosition)
    yPosition += 6
  })

  yPosition += 5

  const tableData = receipt.invoice_line_items.map((item, index) => [
    index + 1,
    item.grn_line_item.purchase_order_line_item.product_name,
    parseFloat(
      item.po_line_item?.unit_price || item.grn_line_item?.purchase_order_line_item?.unit_price
    ).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    parseFloat(item.grn_line_item?.purchase_order_line_item?.quantity).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) +
      " " +
      (item.grn_line_item?.purchase_order_line_item?.unit_of_measurement || "") +
      "s",
    parseFloat(item.grn_line_item.quantity_received).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) +
      " " +
      (item.grn_line_item?.purchase_order_line_item?.unit_of_measurement || "") +
      "s",
    item.discount ? item.discount : "0.00",
    parseFloat(item.grn_line_item.gross_value_received).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    parseFloat(item.grn_line_item.tax_value).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    parseFloat(item.grn_line_item.net_value_received).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
  ])

  autoTable(doc, {
    startY: yPosition,
    head: [
      [
        "SN",
        "Description",
        "Unit Price (NGN)",
        "Ordered Qty",
        "Received Qty",
        "Discount",
        "Gross Total (NGN)",
        "Tax (NGN)",
        "Net Total (NGN)",
      ],
    ],
    body: tableData,
    styles: {
      fontSize: 8,
      cellPadding: 2,
    },
    headStyles: {
      fillColor: Colour ? hexToRgb(Colour) : [53, 19, 241],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 10 },
      1: { halign: "left", cellWidth: 40 },
      2: { halign: "right", cellWidth: 20 },
      3: { halign: "center", cellWidth: 20 },
      4: { halign: "center", cellWidth: 20 },
      5: { halign: "center", cellWidth: 17 },
      6: { halign: "right", cellWidth: 20 },
      7: { halign: "right", cellWidth: 18 },
      8: { halign: "right", cellWidth: 20 },
    },
  })

  const finalY = doc.lastAutoTable.finalY + 10

  doc.setFontSize(12)
  doc.setFont(undefined, "bold")
  doc.text("Total Value Received:", 100, finalY)
  doc.text(
    "NGN " +
      parseFloat(receipt.invoice.gross_total).toLocaleString("en-NG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    195,
    finalY,
    { align: "right" }
  )

  doc.save(`GRN-${receipt?.grn_number || "document"}.pdf`)
}

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [53, 19, 241]
}
